import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDetail } from "@/utils/formatDetail";
import { Loader2, X, FileText, Printer, Mail, Trash2, Plus, IndianRupee, Eye, MessageCircle, FileDown } from "lucide-react";
import { downloadPdf } from "@/utils/downloadPdf";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const waNumber = (mobile) => {
  const digits = String(mobile || "").replace(/\D/g, "");
  if (digits.length === 10) return "91" + digits;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits.length >= 11 ? digits : null;
};

const waReminderLink = (bill, phones) => {
  const num = waNumber(bill.customer?.mobile);
  if (!num) return null;
  const msg = `Dear ${bill.party_name}, a gentle payment reminder from D V Steels: Bill ${bill.bill_no} dated ${bill.bill_date} has a balance due of ${fmt(bill.due)}. Kindly arrange payment at your convenience. Thank you! — D V Steels${phones ? `, Ph: ${phones}` : ""}`;
  return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
};
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};
const today = () => new Date().toISOString().slice(0, 10);
const fyTag = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${String(y).slice(2)}-${String(y + 1).slice(2)}`;
};
const emptyItem = { description: "", qty: 1, rate: 0 };
const METHODS = ["Cash", "Cheque", "UPI", "Bank Transfer"];

export const AdminBilling = ({ client, api, perms = [] }) => {
  const canCreate = perms.includes("bills:create");
  const canEdit = perms.includes("bills:edit");
  const canDelete = perms.includes("bills:delete");
  const canEmail = perms.includes("bills:email");
  const [kind, setKind] = useState("sale");
  const [bills, setBills] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payBill, setPayBill] = useState(null);
  const [payForm, setPayForm] = useState({ date: today(), amount: "", method: "Cash", reference: "" });
  const [payBusy, setPayBusy] = useState(false);
  const [view, setView] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [phones, setPhones] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`${api}/qv2/bills`, { params: { kind } });
      setBills(data);
    } catch {
      toast.error("Could not load bills");
    } finally {
      setLoading(false);
    }
  }, [client, api, kind]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get(`${api}/qv2/customers`);
        setCustomers(data);
      } catch {}
      try {
        const { data } = await client.get(`${api}/qv2/rates`);
        setPhones(data.company?.phones || "");
      } catch {}
    })();
  }, [client, api]);

  const openNew = () => {
    setForm({
      customer_id: "", party_name: "", party_gstin: "", bill_date: today(), due_date: "",
      gst_pct: 18, transporter: "", vehicle_no: "", lr_no: "", lr_date: "",
      items: [{ ...emptyItem }], notes: "",
    });
    setNewOpen(true);
  };

  const setItem = (i, patch) => {
    const items = form.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    setForm({ ...form, items });
  };

  const previewNo = () => {
    const prefix = `DVS/${kind === "sale" ? "SB" : "PB"}/${fyTag(form?.bill_date)}/`;
    const seq = bills.filter((b) => b.bill_no?.startsWith(prefix)).length + 1;
    return `${prefix}${String(seq).padStart(4, "0")}`;
  };

  const subtotal = form ? form.items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0) : 0;
  const gstAmt = subtotal * (Number(form?.gst_pct) || 0) / 100;

  const saveBill = async () => {
    if (kind === "sale" && !form.customer_id) return toast.error("Pick a customer");
    if (kind === "purchase" && !form.party_name.trim()) return toast.error("Enter party / vendor name");
    if (form.items.some((it) => !it.description.trim())) return toast.error("Every line item needs a description");
    setSaving(true);
    try {
      await client.post(`${api}/qv2/bills`, {
        kind,
        customer_id: kind === "sale" ? form.customer_id : null,
        party_name: kind === "purchase" ? form.party_name : null,
        party_gstin: form.party_gstin || null,
        bill_date: form.bill_date,
        due_date: form.due_date || null,
        gst_pct: Number(form.gst_pct) || 0,
        transporter: form.transporter || null,
        vehicle_no: form.vehicle_no || null,
        lr_no: form.lr_no || null,
        lr_date: form.lr_date || null,
        items: form.items.map((it) => ({ description: it.description, qty: Number(it.qty) || 0, rate: Number(it.rate) || 0 })),
        notes: form.notes || null,
      });
      toast.success("Bill saved");
      setNewOpen(false);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not save bill"));
    } finally {
      setSaving(false);
    }
  };

  const openPayments = async (b) => {
    try {
      const { data } = await client.get(`${api}/qv2/bills/${b.id}`);
      setPayBill(data);
      setPayForm({ date: today(), amount: "", method: "Cash", reference: "" });
    } catch {
      toast.error("Could not open bill");
    }
  };

  const recordPayment = async () => {
    if (!Number(payForm.amount)) return toast.error("Enter an amount");
    setPayBusy(true);
    try {
      await client.post(`${api}/qv2/bills/${payBill.id}/payments`, { ...payForm, amount: Number(payForm.amount) });
      toast.success("Payment recorded");
      openPayments(payBill);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not record payment"));
    } finally {
      setPayBusy(false);
    }
  };

  const openView = async (b) => {
    try {
      const { data } = await client.get(`${api}/qv2/bills/${b.id}`);
      setView(data);
    } catch {
      toast.error("Could not open bill");
    }
  };

  const sendEmail = async () => {
    setEmailBusy(true);
    try {
      const { data } = await client.post(`${api}/qv2/bills/${view.id}/email`);
      toast.success(data.message);
      openView(view);
      load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail, "Could not send email"));
    } finally {
      setEmailBusy(false);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete bill ${b.bill_no}?`)) return;
    try {
      await client.delete(`${api}/qv2/bills/${b.id}`);
      toast.success("Bill removed");
      load();
    } catch {
      toast.error("Could not delete");
    }
  };

  const totalSub = bills.reduce((s, b) => s + (b.subtotal || 0), 0);
  const totalAll = bills.reduce((s, b) => s + (b.total || 0), 0);

  return (
    <div data-testid="billing-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex gap-2">
          <button data-testid="billing-tab-sale" onClick={() => setKind("sale")} className={`font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 border transition-colors duration-300 ${kind === "sale" ? "bg-white text-ink border-white" : "border-white/20 text-white/70 hover:text-white"}`}>Sale Bills</button>
          <button data-testid="billing-tab-purchase" onClick={() => setKind("purchase")} className={`font-heading font-semibold uppercase tracking-wider text-sm px-4 py-2 border transition-colors duration-300 ${kind === "purchase" ? "bg-white text-ink border-white" : "border-white/20 text-white/70 hover:text-white"}`}>Purchase Bills</button>
        </div>
        {canCreate && (
          <button data-testid="billing-new-button" onClick={openNew} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2">
            <Plus size={15} /> New {kind === "sale" ? "Sale" : "Purchase"} Bill
          </button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 max-w-2xl">
        <div className="bg-steel border border-white/10 p-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Bill Count</p>
          <p data-testid="billing-count" className="font-heading font-semibold text-2xl text-white mt-1">{bills.length}</p>
        </div>
        <div className="bg-steel border border-white/10 p-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Subtotal</p>
          <p data-testid="billing-subtotal" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(totalSub)}</p>
        </div>
        <div className="bg-steel border border-white/10 p-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Total (incl. GST)</p>
          <p data-testid="billing-total" className="font-heading font-semibold text-2xl text-white mt-1">{fmt(totalAll)}</p>
        </div>
      </div>

      {bills.length === 0 && !loading ? (
        <div data-testid="billing-empty" className="bg-steel border border-white/10 p-14 text-center">
          <FileText size={40} className="mx-auto text-smoke" strokeWidth={1.5} />
          <p className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-smoke">No {kind} bills yet</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-steel overflow-x-auto">
          <table data-testid="billing-table" className="w-full min-w-[1000px]">
            <thead>
              <tr className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke text-left">
                <th className="py-3 px-4 font-medium">Bill #</th>
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">{kind === "sale" ? "Customer" : "Party"}</th>
                <th className="py-3 px-4 font-medium">GSTIN</th>
                <th className="py-3 px-4 font-medium">Subtotal</th>
                <th className="py-3 px-4 font-medium">GST</th>
                <th className="py-3 px-4 font-medium">Total</th>
                <th className="py-3 px-4 font-medium">Paid · Due</th>
                <th className="py-3 px-4 font-medium">Sent</th>
                <th className="py-3 px-4 font-medium">By</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bills.map((b) => (
                <tr key={b.id} data-testid={`bill-row-${b.bill_no}`} className="border-t border-white/10 hover:bg-ink/40 transition-colors duration-300">
                  <td className="py-3.5 px-4 font-mono text-xs text-[#7FB3E8] whitespace-nowrap">{b.bill_no}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{b.bill_date}</td>
                  <td className="py-3.5 px-4 font-heading font-semibold text-base uppercase tracking-wide text-white whitespace-nowrap">{b.party_name}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{b.party_gstin || "—"}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{fmt(b.subtotal)}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke whitespace-nowrap">{fmt(b.gst_amount)}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-white whitespace-nowrap">{fmt(b.total)}</td>
                  <td className="py-3.5 px-4 font-mono text-xs whitespace-nowrap">
                    <span className="text-green-300">{fmt(b.paid)} paid</span>
                    <br />
                    <span className={b.due > 0.01 ? "text-red-300" : "text-smoke"}>{fmt(b.due)} due</span>
                  </td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{b.sent ? "✓" : "—"}</td>
                  <td className="py-3.5 px-4 font-mono text-xs text-smoke">{b.created_by}</td>
                  <td className="py-3.5 px-4 text-right whitespace-nowrap">
                    <button data-testid={`bill-view-${b.bill_no}`} onClick={() => openView(b)} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 px-2 py-1 inline-flex items-center gap-1">
                      <Eye size={13} /> View
                    </button>
                    {canEdit && (
                      <button data-testid={`bill-payments-${b.bill_no}`} onClick={() => openPayments(b)} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white transition-colors duration-300 px-2 py-1 inline-flex items-center gap-1">
                        <IndianRupee size={13} /> Payments
                      </button>
                    )}
                    {kind === "sale" && b.due > 0.01 && (() => {
                      const link = waReminderLink(b, phones);
                      return link ? (
                        <a data-testid={`bill-whatsapp-${b.bill_no}`} href={link} target="_blank" rel="noopener noreferrer" title="Send WhatsApp payment reminder" className="inline-flex p-2 text-green-400 hover:text-green-300 transition-colors duration-300">
                          <MessageCircle size={15} />
                        </a>
                      ) : (
                        <button data-testid={`bill-whatsapp-${b.bill_no}`} onClick={() => toast.error("Customer has no mobile number saved")} title="No mobile saved" aria-label="WhatsApp reminder unavailable" className="inline-flex p-2 text-white/30">
                          <MessageCircle size={15} />
                        </button>
                      );
                    })()}
                    {canDelete && (
                      <button data-testid={`bill-delete-${b.bill_no}`} onClick={() => remove(b)} aria-label="Delete" className="p-2 text-white/50 hover:text-red-400 transition-colors duration-300">
                        <Trash2 size={15} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && form && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="bill-new-modal">
          <div className="max-w-3xl mx-auto bg-steel border border-white/10 p-6 md:p-8 relative">
            <button data-testid="bill-new-close" onClick={() => setNewOpen(false)} aria-label="Close" className="absolute top-4 right-4 p-2 text-white/50 hover:text-white">
              <X size={20} />
            </button>
            <h2 className="font-heading font-semibold uppercase text-2xl tracking-wide text-white mb-6">New {kind === "sale" ? "Sale" : "Purchase"} Bill</h2>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Bill No.</label>
                <input data-testid="bill-no-preview" readOnly value={previewNo()} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-smoke" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Bill Date</label>
                <input data-testid="bill-date-input" type="date" value={form.bill_date} onChange={(e) => setForm({ ...form, bill_date: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white [color-scheme:dark]" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">GST %</label>
                <input data-testid="bill-gst-input" type="number" min="0" max="40" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mb-4">
              {kind === "sale" ? (
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Customer</label>
                  <select
                    data-testid="bill-customer-select"
                    value={form.customer_id}
                    onChange={(e) => {
                      const c = customers.find((x) => x.id === e.target.value);
                      setForm({ ...form, customer_id: e.target.value, party_gstin: c?.gst_no || form.party_gstin });
                    }}
                    className="w-full bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none"
                  >
                    <option value="">Select customer from the list</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.company}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Party / Vendor</label>
                  <input data-testid="bill-party-input" value={form.party_name} onChange={(e) => setForm({ ...form, party_name: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                </div>
              )}
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Party GSTIN</label>
                <input data-testid="bill-gstin-input" value={form.party_gstin} onChange={(e) => setForm({ ...form, party_gstin: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
              </div>
            </div>

            <div className="border border-dashed border-white/15 p-4 mb-4">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke mb-3">Dispatch / E-Way Bill Details (optional)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Transporter</label>
                  <input data-testid="bill-transporter-input" value={form.transporter} onChange={(e) => setForm({ ...form, transporter: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Vehicle No.</label>
                  <input data-testid="bill-vehicle-input" value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">LR / RR No.</label>
                  <input data-testid="bill-lr-input" value={form.lr_no} onChange={(e) => setForm({ ...form, lr_no: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">LR Date</label>
                  <input data-testid="bill-lrdate-input" type="date" value={form.lr_date} onChange={(e) => setForm({ ...form, lr_date: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white [color-scheme:dark]" />
                </div>
              </div>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Line Items</p>
                <button data-testid="bill-add-item" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })} className="font-mono text-[10px] tracking-widest uppercase text-[#7FB3E8] hover:text-white px-2 py-1 inline-flex items-center gap-1">
                  <Plus size={13} /> Add
                </button>
              </div>
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_90px_120px_100px_36px] gap-3 items-end mb-3" data-testid={`bill-item-${i}`}>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Description</label>
                    <input data-testid={`bill-item-desc-${i}`} value={it.description} onChange={(e) => setItem(i, { description: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Qty</label>
                    <input data-testid={`bill-item-qty-${i}`} type="number" min="0" value={it.qty} onChange={(e) => setItem(i, { qty: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Rate ₹</label>
                    <input data-testid={`bill-item-rate-${i}`} type="number" min="0" value={it.rate} onChange={(e) => setItem(i, { rate: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
                  </div>
                  <p className="font-mono text-sm text-white pb-2 text-right">{fmt((Number(it.qty) || 0) * (Number(it.rate) || 0))}</p>
                  <button data-testid={`bill-item-remove-${i}`} onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} disabled={form.items.length === 1} aria-label="Remove item" className="p-2 text-white/50 hover:text-red-400 disabled:opacity-30">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <div className="flex justify-end mt-4">
                <div className="font-body text-sm w-64 text-white">
                  <p className="flex justify-between py-1"><span className="text-smoke">Subtotal</span><span data-testid="bill-preview-subtotal" className="font-mono">{fmt(subtotal)}</span></p>
                  <p className="flex justify-between py-1"><span className="text-smoke">GST @ {form.gst_pct}%</span><span data-testid="bill-preview-gst" className="font-mono">{fmt(gstAmt)}</span></p>
                  <p className="flex justify-between py-2 border-t border-white/20 font-heading font-semibold text-lg"><span>Total</span><span data-testid="bill-preview-total" className="font-mono text-[#7FB3E8]">{fmt(subtotal + gstAmt)}</span></p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Notes</label>
              <textarea data-testid="bill-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-ink border border-white/20 font-body text-sm text-white px-3 py-2 focus:outline-none" />
            </div>

            <div className="flex justify-end gap-3">
              <button data-testid="bill-cancel-button" onClick={() => setNewOpen(false)} className="font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 border border-white/20 text-white/70 hover:text-white transition-colors duration-300">Cancel</button>
              <button data-testid="bill-save-button" onClick={saveBill} disabled={saving} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : "Save Bill"}
              </button>
            </div>
          </div>
        </div>
      )}

      {payBill && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="payments-modal">
          <div className="max-w-lg mx-auto bg-steel border border-white/10 p-6 md:p-8 relative">
            <button data-testid="payments-close" onClick={() => setPayBill(null)} aria-label="Close" className="absolute top-4 right-4 p-2 text-white/50 hover:text-white">
              <X size={20} />
            </button>
            <h2 className="font-heading font-semibold uppercase text-xl tracking-wide text-white mb-5">Payments · {payBill.bill_no}</h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="border border-white/10 p-3 text-center">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Bill Total</p>
                <p className="font-mono text-sm text-white mt-1">{fmt(payBill.total)}</p>
              </div>
              <div className="border border-white/10 p-3 text-center">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Paid</p>
                <p data-testid="payments-paid" className="font-mono text-sm text-green-300 mt-1">{fmt(payBill.paid)}</p>
              </div>
              <div className="border border-white/10 p-3 text-center">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Due</p>
                <p data-testid="payments-due" className="font-mono text-sm text-red-300 mt-1">{fmt(payBill.due)}</p>
              </div>
            </div>

            {(payBill.payments || []).length > 0 && (
              <div className="mb-6">
                {payBill.payments.map((p) => (
                  <div key={p.id} className="flex justify-between border-b border-white/10 py-2 font-mono text-xs text-smoke" data-testid={`payment-row-${p.id.slice(0, 8)}`}>
                    <span>{p.date}</span>
                    <span>{p.method}{p.reference ? ` · ${p.reference}` : ""}</span>
                    <span className="text-green-300">{fmt(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Date</label>
                <input data-testid="payment-date-input" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white [color-scheme:dark]" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Amount ₹</label>
                <input data-testid="payment-amount-input" type="number" min="0" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Method</label>
                <select data-testid="payment-method-select" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="w-full bg-ink border border-white/20 font-mono text-sm text-white px-3 py-2.5 focus:outline-none">
                  {METHODS.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">Reference</label>
                <input data-testid="payment-reference-input" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="input-line w-full bg-transparent border-b border-white/20 py-2 font-mono text-sm text-white" />
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-3">
              {payBill.due > 0.01 && waReminderLink(payBill, payBill.company?.phones || phones) && (
                <a
                  data-testid="payments-whatsapp-button"
                  href={waReminderLink(payBill, payBill.company?.phones || phones)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 border border-green-500/50 text-green-300 hover:bg-green-600 hover:text-white transition-colors duration-300 flex items-center gap-2"
                >
                  <MessageCircle size={15} /> WhatsApp Reminder
                </a>
              )}
              <button data-testid="payments-close-button" onClick={() => setPayBill(null)} className="font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 border border-white/20 text-white/70 hover:text-white transition-colors duration-300">Close</button>
              <button data-testid="payment-record-button" onClick={recordPayment} disabled={payBusy} className="btn-sweep btn-navy font-heading font-semibold uppercase tracking-wider text-sm px-6 py-2.5 flex items-center gap-2 disabled:opacity-60">
                {payBusy ? <Loader2 size={15} className="animate-spin" /> : "Record Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto p-4 md:p-10" data-testid="bill-view-modal">
          <div className="quote-sheet max-w-3xl mx-auto bg-white text-ink p-8 md:p-12 relative">
            <button data-testid="bill-view-close" onClick={() => setView(null)} aria-label="Close" className="no-print absolute top-4 right-4 p-2 text-ink/50 hover:text-ink">
              <X size={22} />
            </button>
            <div className="no-print flex flex-wrap items-center gap-2 mb-6">
              <button data-testid="bill-print-button" onClick={() => window.print()} className="bg-royal text-white font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-ink transition-colors duration-300">
                <Printer size={15} /> Print / Save PDF
              </button>
              <button data-testid="bill-pdf-button" onClick={() => downloadPdf(client, `${api}/qv2/bills/${view.id}/pdf`, `${view.bill_no.replace(/\//g, "-")}.pdf`)} className="border border-royal text-royal font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-royal hover:text-white transition-colors duration-300">
                <FileDown size={15} /> Download PDF
              </button>
              {view.kind === "sale" && canEmail && (
                <button data-testid="bill-email-button" onClick={sendEmail} disabled={emailBusy} className="border border-royal text-royal font-heading font-semibold uppercase tracking-wider text-sm px-5 py-2.5 flex items-center gap-2 hover:bg-royal hover:text-white transition-colors duration-300 disabled:opacity-60">
                  {emailBusy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email to Customer
                </button>
              )}
            </div>

            <div className="border-b-2 border-royal pb-5 mb-6">
              {view.company?.logo && <img src={view.company.logo} alt="Company logo" className="h-14 mb-3" />}
              <p className="font-heading font-semibold uppercase text-3xl text-royal">{view.company?.name}</p>
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/60 mt-1">{view.company?.tagline}</p>
              <p className="font-body text-xs text-ink/60 mt-2">{view.company?.address} · {view.company?.phones} · {view.company?.email}{view.company?.gstin ? ` · GSTIN: ${view.company.gstin}` : ""}</p>
            </div>

            <div className="flex justify-between mb-6 font-body text-sm">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50">{view.kind === "sale" ? "Bill To" : "From"}</p>
                <p className="font-heading font-semibold uppercase text-xl">{view.party_name}</p>
                {view.party_gstin && <p className="text-ink/60 text-xs">GSTIN: {view.party_gstin}</p>}
                {view.customer?.site && <p className="text-ink/60 text-xs">{view.customer.site}</p>}
              </div>
              <div className="text-right">
                <p className="font-mono text-xs text-royal">{view.bill_no}</p>
                <p className="text-ink/60 text-xs">Date: {fmtDate(view.bill_date)}</p>
                {view.due_date && <p className="text-ink/60 text-xs">Due: {fmtDate(view.due_date)}</p>}
              </div>
            </div>

            {(view.dispatch?.transporter || view.dispatch?.vehicle_no || view.dispatch?.lr_no) && (
              <div className="border border-ink/15 p-3 mb-6 font-body text-xs text-ink/70 flex flex-wrap gap-x-6 gap-y-1">
                {view.dispatch.transporter && <span><b>Transporter:</b> {view.dispatch.transporter}</span>}
                {view.dispatch.vehicle_no && <span><b>Vehicle:</b> {view.dispatch.vehicle_no}</span>}
                {view.dispatch.lr_no && <span><b>LR/RR:</b> {view.dispatch.lr_no}</span>}
                {view.dispatch.lr_date && <span><b>LR Date:</b> {view.dispatch.lr_date}</span>}
              </div>
            )}

            <table className="w-full mb-6">
              <thead>
                <tr className="border-b border-ink/20 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/60 text-left">
                  <th className="py-2 pr-2">Description</th>
                  <th className="py-2 pr-2">Qty</th>
                  <th className="py-2 pr-2 text-right">Rate</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {view.items?.map((it, i) => (
                  <tr key={i} className="border-b border-ink/10 font-body text-sm">
                    <td className="py-2.5 pr-2">{it.description}</td>
                    <td className="py-2.5 pr-2 font-mono text-xs">{it.qty}</td>
                    <td className="py-2.5 pr-2 font-mono text-xs text-right">{fmt(it.rate)}</td>
                    <td className="py-2.5 font-mono text-xs text-right">{fmt(it.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex justify-end mb-6">
              <div className="font-body text-sm w-64">
                <p className="flex justify-between py-1"><span className="text-ink/60">Subtotal</span><span className="font-mono">{fmt(view.subtotal)}</span></p>
                <p className="flex justify-between py-1"><span className="text-ink/60">GST ({view.gst_pct}%)</span><span className="font-mono">{fmt(view.gst_amount)}</span></p>
                <p className="flex justify-between py-2 border-t border-ink/20 font-heading font-semibold text-lg"><span>Total</span><span className="font-mono text-royal">{fmt(view.total)}</span></p>
                <p className="flex justify-between py-1"><span className="text-ink/60">Paid</span><span className="font-mono text-green-700">{fmt(view.paid)}</span></p>
                <p className="flex justify-between py-1 font-heading font-semibold"><span>Balance Due</span><span data-testid="bill-view-due" className="font-mono text-red-700">{fmt(view.due)}</span></p>
              </div>
            </div>

            {view.notes && <p className="font-body text-sm text-ink/70 mb-4"><span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50 block mb-1">Notes</span>{view.notes}</p>}
            {view.terms?.length > 0 && (
              <div className="border-t border-ink/20 pt-4">
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50 mb-2">Terms & Conditions</p>
                <ol className="font-body text-xs text-ink/70 list-decimal list-inside flex flex-col gap-1">
                  {view.terms.map((t, i) => <li key={i}>{t}</li>)}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
